import { type Key, useMemo, useState } from 'react';
import { Input } from './Input';
import { LoadingLabel } from './LoadingLabel';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from './shadcn/Command';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/Popover';

interface PopSearchProps<T> {
  value: Key | null;
  onChange: (value: T | null) => void;
  options: T[];
  trigger: (trigger: () => void, selectedOption: T | null) => React.ReactNode;
  valueAccessor: (item: T) => Key;
  labelAccessor: (item: T) => string;
  itemOverride?: (item: T) => React.ReactNode;
  placeholder?: string;
  onSearchChange?: (search: string) => void;
  isLoading?: boolean;
}

export function PopSearch<T>(props: PopSearchProps<T>) {
  const {
    value,
    onChange,
    options,
    valueAccessor,
    labelAccessor,
    itemOverride,
    trigger,
    placeholder,
    onSearchChange,
    isLoading = false,
  } = props;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      labelAccessor(option).toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search, labelAccessor]);
  const selectedOption = useMemo(
    () => options.find((option) => valueAccessor(option) === value) ?? null,
    [value, options, valueAccessor]
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger(() => setOpen(true), selectedOption)}</PopoverTrigger>
      <PopoverContent className="p-0" side="right" align="start">
        <Command shouldFilter={false}>
          <Input
            placeholder={placeholder ?? 'Selectionner...'}
            onChange={(e) => {
              setSearch(e.target.value);
              onSearchChange?.(e.target.value);
            }}
            noBorder
          />
          <CommandList>
            <CommandEmpty>{isLoading ? <LoadingLabel /> : 'Aucun résultat.'}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={valueAccessor(option)}
                  value={valueAccessor(option).toString()}
                  onSelect={(value) => {
                    setOpen(false);
                    const option = options.find(
                      (option) => valueAccessor(option).toString() === value
                    );
                    onChange(option ?? null);
                  }}
                >
                  {itemOverride ? itemOverride(option) : labelAccessor(option)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
